// src/customer/customer-policy/customer-policy-commission/CommissionReport.tsx

import React from "react";
import { Panel, Select, Row, Column } from "../../../components";
import { Badge } from "primereact/badge";
import { FloatingLabel, Form } from "react-bootstrap";
import { ICommissionReportData } from "./models/CommissionTypes";
import { fomratWithCommasAndDecimals } from "../../../shared/util/currencyUtils";
import { rateOrEmpty } from "./commissionFormatters";

interface ICommissionReportProps {
  data: ICommissionReportData;
  transEffectiveDate: string;
}

const CommissionReport: React.FC<ICommissionReportProps> = ({
  data,
  transEffectiveDate,
}) => {
  return (
    <Panel
      title="TRANSACTION DETAILS"
      className="commission-panel-header"
      panelHeaderItems={
        <>
          {data.badges.map((badge, index) => (
            <Badge key={index} severity="info" value={badge} className="p-badge p-component p-badge-no-gutter ul-truncate-p-badge" />
          ))}
          <span className="commission-card-date ms-auto">
            TRANS EFFECTIVE DATE {transEffectiveDate}
          </span>
        </>
      }
    >
      {/* Transaction Details Fields */}
      <Row className="mb-3">
          <Column lg={4} md={4}>
            <FloatingLabel controlId="originalTransactionCode" label="Original Transaction Code">
              <Form.Control
                type="text"
                name="originalTransactionCode"
                value={data.originalTransactionCode ?? ""}
                placeholder="Original Transaction Code"
                onChange={() => {}}
              />
            </FloatingLabel>
          </Column>
          <Column lg={4} md={4}>
            <FloatingLabel controlId="premium" label="Premium">
              <Form.Control
                type="text"
                name="premium"
                value={`$${fomratWithCommasAndDecimals(data.premium)}`}
                placeholder="Premium"
                onChange={() => {}}
              />
            </FloatingLabel>
          </Column>
          <Column lg={4} md={4}>
            <FloatingLabel controlId="nonHurricanePremium" label="Non-Hurricane Premium">
              <Form.Control
                type="text"
                name="nonHurricanePremium"
                value={`$${fomratWithCommasAndDecimals(data.nonHurricanePremium)}`}
                placeholder="Non-Hurricane Premium"
                onChange={() => {}}
              />
            </FloatingLabel>
          </Column>
        </Row>
        <Row className="mb-3">
          <Column lg={4} md={4}>
            <FloatingLabel controlId="hurricanePremium" label="Hurricane Premium">
              <Form.Control
                type="text"
                name="hurricanePremium"
                value={`$${fomratWithCommasAndDecimals(data.hurricanePremium)}`}
                placeholder="Hurricane Premium"
                onChange={() => {}}
              />
            </FloatingLabel>
          </Column>
          <Column lg={4} md={4}>
            <FloatingLabel controlId="eqPrem" label="EQ Prem">
              <Form.Control
                type="text"
                name="eqPrem"
                value={`$${fomratWithCommasAndDecimals(data.eqPrem)}`}
                placeholder="EQ Prem"
                onChange={() => {}}
              />
            </FloatingLabel>
          </Column>
          <Column lg={4} md={4}>
            <FloatingLabel controlId="totalPremium" label="Total Premium">
              <Form.Control
                type="text"
                name="totalPremium"
                value={`$${fomratWithCommasAndDecimals(data.totalPremium)}`}
                placeholder="Total Premium"
                onChange={() => {}}
              />
            </FloatingLabel>
          </Column>
        </Row>

        {/* Commissions Section */}
        <h6 className="commission-section-header roboto-condensed-bold">COMMISSIONS</h6>
        <Row className="mb-3">
          <Column lg={4} md={4}>
            <FloatingLabel controlId="commissionRate" label="Commission Rate">
              <Form.Control
                type="text"
                name="commissionRate"
                value={`${data.commissions.commissionRate}%`}
                placeholder="Commission Rate"
                onChange={() => {}}
              />
            </FloatingLabel>
          </Column>
          <Column lg={4} md={4}>
            <FloatingLabel controlId="commission" label="Commission">
              <Form.Control
                type="text"
                name="commission"
                value={`$${fomratWithCommasAndDecimals(data.commissions.commission)}`}
                placeholder="Commission"
                onChange={() => {}}
              />
            </FloatingLabel>
          </Column>
          <Column lg={4} md={4}>
            <FloatingLabel controlId="windExclusion" label="Wind Exclusion">
              <Form.Control
                type="text"
                name="windExclusion"
                value={`$${fomratWithCommasAndDecimals(data.commissions.windExclusion)}`}
                placeholder="Wind Exclusion"
                onChange={() => {}}
              />
            </FloatingLabel>
          </Column>
        </Row>
        <Row className="mb-3">
          <Column lg={4} md={4}>
            <FloatingLabel controlId="commissionRate2" label="Commission Rate 2">
              <Form.Control
                type="text"
                name="commissionRate2"
                value={rateOrEmpty(data.commissions.commissionRate2)}
                placeholder="Commission Rate 2"
                onChange={() => {}}
              />
            </FloatingLabel>
          </Column>
          <Column lg={4} md={4}>
            <FloatingLabel controlId="commissionAmount" label="Commission Amount">
              <Form.Control
                type="text"
                name="commissionAmount"
                value={`$${fomratWithCommasAndDecimals(data.commissions.commissionAmount)}`}
                placeholder="Commission Amount"
                onChange={() => {}}
              />
            </FloatingLabel>
          </Column>
          <Column lg={4} md={4}>
            {/* TODO: Wire up onChange and options from API — #99152 */}
            <Select
              id="commission-calc-type"
              label="Commission Calc Type"
              options={[{ label: "Choose...", value: "" }]}
              defaultSelected={[]}
              disabled={true}
              placeholder="Choose..."
              showAsFloatingLabel={false}
            />
          </Column>
        </Row>
        <Row className="mb-3">
          <Column lg={4} md={4}>
            <FloatingLabel controlId="commissionRateNonAdmittee" label="Comm Rate Non-Admittee">
              <Form.Control
                type="text"
                name="commissionRateNonAdmittee"
                value={rateOrEmpty(data.commissions.commissionRateNonAdmittee)}
                placeholder="Comm Rate Non-Admittee"
                onChange={() => {}}
              />
            </FloatingLabel>
          </Column>
          <Column lg={4} md={4}>
            <FloatingLabel controlId="commissionNonAdmittee" label="Commission Non-Admittee">
              <Form.Control
                type="text"
                name="commissionNonAdmittee"
                value={`$${fomratWithCommasAndDecimals(data.commissions.commissionNonAdmittee)}`}
                placeholder="Commission Non-Admittee"
                onChange={() => {}}
              />
            </FloatingLabel>
          </Column>
          <Column lg={4} md={4}>
            <FloatingLabel controlId="commissionRateEQ" label="Commission Rate EQ">
              <Form.Control
                type="text"
                name="commissionRateEQ"
                value={`${data.commissions.commissionRateEQ}%`}
                placeholder="Commission Rate EQ"
                onChange={() => {}}
              />
            </FloatingLabel>
          </Column>
        </Row>
        <Row className="mb-3">
          <Column lg={4} md={4}>
            <FloatingLabel controlId="agentEQCommAmt" label="Agent EQ Comm Amt">
              <Form.Control
                type="text"
                name="agentEQCommAmt"
                value={`$${fomratWithCommasAndDecimals(data.commissions.agentEQCommAmt)}`}
                placeholder="Agent EQ Comm Amt"
                onChange={() => {}}
              />
            </FloatingLabel>
          </Column>
          <Column lg={4} md={4}>
            <FloatingLabel controlId="commissionRateNonHurricane" label="Commission Rate Non Hurricane">
              <Form.Control
                type="text"
                name="commissionRateNonHurricane"
                value={`${data.commissions.commissionRateNonHurricane}%`}
                placeholder="Commission Rate Non Hurricane"
                onChange={() => {}}
              />
            </FloatingLabel>
          </Column>
          <Column lg={4} md={4}>
            <FloatingLabel controlId="commissionRateHurricane" label="Commission Rate Hurricane">
              <Form.Control
                type="text"
                name="commissionRateHurricane"
                value={`$${fomratWithCommasAndDecimals(data.commissions.commissionRateHurricane)}`}
                placeholder="Commission Rate Hurricane"
                onChange={() => {}}
              />
            </FloatingLabel>
          </Column>
        </Row>

        {/* Fees Section */}
        <h6 className="commission-section-header roboto-condensed-bold">FEES</h6>
        <Row>
          <Column lg={6} md={6}>
            <FloatingLabel controlId="totalFees" label="Total Fees">
              <Form.Control
                type="text"
                name="totalFees"
                value={`$${fomratWithCommasAndDecimals(data.fees.totalFees)}`}
                placeholder="Total Fees"
                onChange={() => {}}
              />
            </FloatingLabel>
          </Column>
        </Row>
    </Panel>
  );
};

export default CommissionReport;
